// The MAP PACK reader (smooth Map; PROTOCOL.md "Map pack (pre-entry Map)"):
// `meta.mappack.streets` = mappack.bin, the streets + trails of the bake's
// ways.json (+ the pathinfo extras) as a ~230 KB binary, so the Map before
// entry draws them seconds before the 35 MB ways.json is fetched and decoded.
// The Map uses them until WaysNet's own graph / trail index is in (main.js
// mapSrc.graph, playerMap.setTrails); the Map's picture is the device's own
// (mapclass.js). A bake without meta.mappack: null, the Map waits for
// ways.json as before.
//
// decodeMapPack(ab) → {graph: {ways: [{pts: [[x, 0, z]…], kind 'road' |
// 'rail', name, run}]}, trails: [entry], extras: [entry]} — the shapes
// maplayers.js paintRoads / streetLabels and PlayerMap.setTrails /
// setTrailExtras read (a trail entry as trailmarks.js buildTrailIndex makes
// it, an extra as prepExtra does: pts Float32Array xz, bbox, mark = markOf).
// Throws on any malformed field (the Python reference: pipeline/mappack.py
// decode). Pure: no DOM (scripts/check-mapfirst.mjs runs it in Node).
// decodeMapPackSteps = THE decoder, a step per STEP runs (R8 M1: the whole
// pack in one go was part of a 56-63 ms task); decodeMapPack = its drain.
import { fetchAsset } from './assetq.js';
import { runSteps } from './decode.js';
import { markOf } from './trailmarks.js';

const MAGIC = 0x31504d56;     // 'VMP1' little-endian
const FORMAT = 'vrm-mappack', REV = 1;
const NONE16 = 0xffff, ABS32 = 1;
const SECTIONS = [['cls', 'u1'], ['sac', 'u1'], ['off', 'u1'], ['oa', 'u1'], ['bel', 'u1'], ['flags', 'u1'],
  ['name', 'u2'], ['vf', 'u2'], ['n', 'u4'], ['run', 'i4'], ['origin', 'i4'], ['delta', 'i2'], ['abs', 'i4']];
const ARR = { u1: Uint8Array, u2: Uint16Array, u4: Uint32Array, i4: Int32Array, i2: Int16Array };
const STEP = 1024;              // runs per step
const ROAD = new Set(['major', 'minor']), RAIL = new Set(['rail', 'funicular']), TRAIL = new Set(['path', 'stairs']);

export function decodeMapPack(ab) {
  const it = decodeMapPackSteps(ab);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}

export function* decodeMapPackSteps(ab) {
  const bad = (m) => { throw new Error(`map pack: ${m}`); };
  if (!(ab instanceof ArrayBuffer) || ab.byteLength < 8) bad('too short');
  const dv = new DataView(ab);
  if (dv.getUint32(0, true) !== MAGIC) bad('bad magic');
  const H = dv.getUint32(4, true);
  if (H % 4 || 8 + H > ab.byteLength) bad('bad header length');
  const head = JSON.parse(new TextDecoder().decode(new Uint8Array(ab, 8, H)));
  if (head.format !== FORMAT || head.rev !== REV) bad(`format ${head.format} rev ${head.rev}`);
  const secs = head.sections || [];
  if (secs.map((s) => s.name).join() !== SECTIONS.map((s) => s[0]).join()) bad('sections out of order');
  const base = 8 + H, A = {};
  SECTIONS.forEach(([k, t], i) => {
    const s = secs[i], T = ARR[t];
    if (s.offset % 4 || base + s.offset + s.bytes > ab.byteLength || s.bytes !== s.count * T.BYTES_PER_ELEMENT) bad(`section ${k} out of bounds`);
    // every offset is 4-aligned past an 8 + H (H % 4 = 0) start: a view, no copy
    A[k] = new T(ab, base + s.offset, s.count);
  });
  const R = head.runs;
  for (const [k] of SECTIONS.slice(0, 10)) if (A[k].length !== R) bad(`section ${k} has ${A[k].length} entries, not ${R}`);
  if (A.origin.length !== 2 * R) bad('origin size');
  const S = head.strings || [], q = head.quantum_m;
  const str = (i) => (i === NONE16 ? null : S[i]);
  const ways = [], trails = [], extras = [];
  let di = 0, ai = 0, verts = 0;
  for (let i = 0; i < R; i++) {
    if (i && i % STEP === 0) yield;
    const n = A.n[i];
    if (n < 2) bad(`run ${i}: ${n} vertices`);
    const pts = new Float32Array(2 * n);
    if (A.flags[i] & ABS32) {
      if (ai + 2 * n > A.abs.length) bad(`run ${i}: abs short`);
      for (let j = 0; j < 2 * n; j++) pts[j] = A.abs[ai + j] * q;
      ai += 2 * n;
    } else {
      if (di + 2 * (n - 1) > A.delta.length) bad(`run ${i}: delta short`);
      let x = A.origin[2 * i], z = A.origin[2 * i + 1];
      pts[0] = x * q; pts[1] = z * q;
      for (let j = 1; j < n; j++) {
        x += A.delta[di++]; z += A.delta[di++];
        pts[2 * j] = x * q; pts[2 * j + 1] = z * q;
      }
    }
    verts += n;
    const cls = head.cls[A.cls[i]], name = str(A.name[i]) || '', run = A.run[i];
    if (cls === undefined) bad(`run ${i}: cls ${A.cls[i]}`);
    if (ROAD.has(cls) || RAIL.has(cls)) {
      const P = new Array(n);
      for (let j = 0; j < n; j++) P[j] = [pts[2 * j], 0, pts[2 * j + 1]];
      ways.push({ pts: P, kind: ROAD.has(cls) ? 'road' : 'rail', name: ROAD.has(cls) ? name : '', run });
      continue;
    }
    const vf = str(A.vf[i]);
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let j = 0; j < 2 * n; j += 2) {
      if (pts[j] < x0) x0 = pts[j]; if (pts[j] > x1) x1 = pts[j];
      if (pts[j + 1] < z0) z0 = pts[j + 1]; if (pts[j + 1] > z1) z1 = pts[j + 1];
    }
    const t = { run, name, sac: A.sac[i] || null, off: head.off[A.off[i]] || null, oa: head.oa[A.oa[i]] || null,
      bel: head.bel[A.bel[i]] || null, tt: vf !== null ? { vf } : null, mark: 'none', pts, bbox: [x0, z0, x1, z1] };
    t.mark = markOf(t);
    if (cls === 'extra') { t.extra = true; extras.push(t); } else if (TRAIL.has(cls)) trails.push(t);
    else bad(`run ${i}: cls ${cls}`);
  }
  if (di !== A.delta.length || ai !== A.abs.length || verts !== head.verts) bad('vertex sections do not add up');
  return { graph: { ways }, trails, extras, head };
}

// meta.mappack.streets through the asset queue (class `map`: the Map draws
// it) → decodeMapPack (in steps, `map.pack`), or null (no pack in this bake / not served / bad)
export async function loadMapPack(meta, baseUrl) {
  const m = meta && meta.mappack && meta.mappack.streets;
  if (!m || !m.file) return null;
  try {
    const ab = await fetchAsset(`${baseUrl}/${m.file}`, { prio: 'map', as: 'arrayBuffer' });
    if (!ab) { console.warn(`[mappack] ${m.file}: not served — the Map waits for ways.json`); return null; }
    return await runSteps(decodeMapPackSteps(ab), 'map.pack');
  } catch (e) {
    console.warn(`[mappack] ${m.file}: ${e.message} — the Map waits for ways.json`);
    return null;
  }
}
